import { useEffect, useRef, useState } from "react";
import { Share2, Link2, Check, MessageCircle, Facebook } from "lucide-react";
import { toast } from "sonner";

// Lightweight share menu for the product page.
// Uses native Web Share API when available; always offers explicit channels + copy link.
export const ShareMenu = ({ url, title }) => {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const ref = useRef(null);

  const shareUrl = url || (typeof window !== "undefined" ? window.location.href : "");
  const text = title ? `Check out ${title} at Kurti Connection` : "Check this out at Kurti Connection";
  const enc = encodeURIComponent;

  useEffect(() => {
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const channels = [
    {
      key: "whatsapp",
      label: "WhatsApp",
      icon: <MessageCircle size={16} strokeWidth={1.8} />,
      href: `https://wa.me/?text=${enc(`${text} ${shareUrl}`)}`,
    },
    {
      key: "facebook",
      label: "Facebook",
      icon: <Facebook size={16} strokeWidth={1.8} />,
      href: `https://www.facebook.com/sharer/sharer.php?u=${enc(shareUrl)}`,
    },
    {
      key: "x",
      label: "X (Twitter)",
      icon: <span className="font-display text-[15px] leading-none w-4 text-center">𝕏</span>,
      href: `https://twitter.com/intent/tweet?text=${enc(text)}&url=${enc(shareUrl)}`,
    },
    {
      key: "pinterest",
      label: "Pinterest",
      icon: <span className="font-mono text-[13px] leading-none w-4 text-center">P</span>,
      href: `https://pinterest.com/pin/create/button/?url=${enc(shareUrl)}&description=${enc(text)}`,
    },
  ];

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success("Link copied");
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error("Couldn't copy link");
    }
  };

  const handleClick = async () => {
    if (typeof navigator !== "undefined" && navigator.share) {
      try {
        await navigator.share({ title: title || "Kurti Connection", text, url: shareUrl });
        return;
      } catch {
        // user cancelled or unsupported — fall back to menu
      }
    }
    setOpen((v) => !v);
  };

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={handleClick}
        data-testid="share-button"
        aria-label="Share this product"
        className="w-12 h-12 flex items-center justify-center border border-[#E5E5DF] hover:border-[#1A1A1A] transition-colors"
      >
        <Share2 size={17} strokeWidth={1.8} />
      </button>

      {open && (
        <div
          data-testid="share-menu"
          className="absolute right-0 top-14 z-30 w-52 bg-[#FFFFFF] border border-[#E5E5DF] shadow-lg py-1.5"
        >
          {channels.map((c) => (
            <a
              key={c.key}
              href={c.href}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setOpen(false)}
              data-testid={`share-${c.key}`}
              className="flex items-center gap-3 px-4 py-2.5 text-sm text-[#1A1A1A] hover:bg-[#EFEFEA] transition-colors"
            >
              <span className="text-[#B84A3B]">{c.icon}</span>
              {c.label}
            </a>
          ))}
          <div className="h-px bg-[#E5E5DF] my-1" />
          <button
            type="button"
            onClick={copyLink}
            data-testid="share-copy-link"
            className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-[#1A1A1A] hover:bg-[#EFEFEA] transition-colors"
          >
            <span className="text-[#B84A3B]">{copied ? <Check size={16} strokeWidth={1.8} /> : <Link2 size={16} strokeWidth={1.8} />}</span>
            {copied ? "Copied!" : "Copy link"}
          </button>
        </div>
      )}
    </div>
  );
};
